import { useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { Link, useParams } from 'react-router';
import type { ActionDetail } from '@isg/shared-types';
import {
  AKSIYON_DURUM_ETIKETI,
  AKSIYON_IZ_ETIKETI,
  AKSIYON_NITELIK_ETIKETI,
  aksiyonNiteligi,
} from '@isg/validation';
import {
  useAksiyon,
  useAksiyonDuzeltme,
  useAksiyonGecis,
  useAksiyonIzi,
  useAtanabilirler,
} from '../api/queries';
import { AksiyonEkBolumu } from './aksiyon-ek-parts';
import { QueryStatus } from './parts';
import { Bolum, CokenBolum, KayitDetayi, Rozet, SatirEylemleri } from '../sozlesme';
import { ANLAM } from '../theme';
import { gunGirdisiIso, tarihGoster, zamanGoster } from '../lib/format';

/**
 * AKSİYON DETAYI (T4 — tur 2).
 *
 * ⚠️ **VARLIK SEBEBİ.** `GET /api/actions/:id` ucu F2·S2'den beri vardı ve
 * HİÇBİR EKRAN ÇAĞIRMIYORDU: aksiyonun bilgisi liste satırına sıkışmıştı,
 * açıklaması ve kaynak bağı hiçbir yerde görünmüyordu. Yönerge §6/9-h'nin
 * *"belgede ✅ kodda yok"* sorusunun bu modüldeki cevabı buydu.
 *
 * ⚠️ **SEKME YOK — BÖLÜMLER ALT ALTA** (P5, `KayitDetayi`).
 * ⚠️ **KÜNYE IZGARADA** (P9): kısa alanlar yan yana, açıklama tam genişlikte.
 * ⚠️ **EYLEMLER BAŞLIKTA** (P2-c): bir birincil + ⋮; görünürlük SUNUCUDAN
 * (`yetkiler`) — ekran ikinci kural yazmaz (§2.40).
 */
export function AksiyonDetayScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const aksiyon = useAksiyon(id);

  return (
    <Stack>
      <QueryStatus queries={[aksiyon]} kayit />
      {aksiyon.isSuccess && <Detay aksiyon={aksiyon.data} />}
    </Stack>
  );
}

/**
 * ⚠️ **İPTAL, KAPALIYLA AYNI RENKTE DEĞİLDİR** (V3): kapanış `olumlu`dur
 * (iş bitti), iptal ise bir vazgeçiştir — `bilgi` (nötr). Aynı renge
 * basmak, raporda ayrı sayılan iki gerçeği ekranda tek gerçeğe indirirdi.
 */
function durumAnlami(durum: ActionDetail['durum']): 'bilgi' | 'dikkat' | 'olumlu' {
  if (durum === 'open') return 'bilgi';
  if (durum === 'completed_reported') return 'dikkat';
  if (durum === 'cancelled') return 'bilgi';
  return 'olumlu';
}

function Detay({ aksiyon }: { aksiyon: ActionDetail }): React.JSX.Element {
  const nitelik = aksiyonNiteligi(aksiyon);
  const gecis = useAksiyonGecis();
  const [acikForm, setAcikForm] = useState<DuzeltmeIslemi | null>(null);
  // ⚠️ İPTAL EDİLEN aksiyon GECİKMİŞ SAYILMAZ: iptal terminaldir ve
  // termini artık bir taahhüt değildir (kapalı ile aynı sınıf).
  // ✏️ MADDE 299: işaret SUNUCUDAN (`terminGecti` — termin günü son gündür,
  // saha gününe göre). Ekran termini bugünle karşılaştırmaz: tarayıcının günü
  // saha günü olmayabilir ve liste süzgeciyle ayrışırdı.
  const gecikti = aksiyon.terminGecti;

  return (
    <KayitDetayi
      testId="aksiyon-detay"
      geriYol="/aksiyonlar"
      geriEtiket="Aksiyonlara dön"
      baslik={aksiyon.baslik}
      ustBilgi={
        <Group gap="xs">
          <Rozet anlam={durumAnlami(aksiyon.durum)} testId="aksiyon-durum">
            {AKSIYON_DURUM_ETIKETI[aksiyon.durum]}
          </Rozet>
          {gecikti && (
            <Rozet anlam="gecikmis" testId="termin-gecikti">
              Termin geçti
            </Rozet>
          )}
        </Group>
      }
      eylemler={
        <SatirEylemleri
          testId="aksiyon-detay-eylemleri"
          birincil={
            aksiyon.yetkiler.kapatabilir
              ? {
                  etiket: 'Onayla-kapat',
                  testId: 'detay-onayla-kapat',
                  pasif: gecis.isPending,
                  onClick: () => gecis.mutate({ id: aksiyon.id, gecis: 'approve-close' }),
                }
              : undefined
          }
          digerleri={[
            {
              etiket: 'Geri aç',
              testId: 'detay-geri-ac',
              gizli: !aksiyon.yetkiler.geriAcabilir,
              pasif: gecis.isPending,
              onClick: () => gecis.mutate({ id: aksiyon.id, gecis: 'reopen' }),
            },
            // V6 (Tur 2½b — G12): düzeltmeler ⋮ ALTINDA. Görünürlük TEK
            // bayraktan (`duzeltebilir`) — ekran ikinci kural yazmaz ve
            // görünmeyen düğmenin ucu da aynı kapıdan reddeder (§2.40).
            {
              etiket: 'Sorumluyu değiştir',
              testId: 'detay-yeniden-ata',
              gizli: !aksiyon.yetkiler.duzeltebilir,
              onClick: () => setAcikForm('reassign'),
            },
            {
              etiket: 'Termini değiştir',
              testId: 'detay-termin-degistir',
              gizli: !aksiyon.yetkiler.duzeltebilir,
              onClick: () => setAcikForm('change-deadline'),
            },
            {
              etiket: 'Başlığı düzelt',
              testId: 'detay-duzelt',
              // V5 DAHA DAR: yalnız AÇIK durumda — tamamlandı bildirilmiş
              // işin başlığını değiştirmek, sorumlunun neyi bitirdiğini
              // sonradan değiştirmektir.
              gizli: !aksiyon.yetkiler.duzeltebilir || aksiyon.durum !== 'open',
              onClick: () => setAcikForm('edit'),
            },
            {
              etiket: 'İptal et',
              testId: 'detay-iptal',
              tehlikeli: true,
              gizli: !aksiyon.yetkiler.duzeltebilir,
              onClick: () => setAcikForm('cancel'),
            },
          ]}
        />
      }
    >
      {gecis.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="aksiyon-detay-hata">
          {gecis.error.message}
        </Text>
      )}

      {/* P9 — KISA ALANLAR IZGARADA, uzun alan (açıklama) tam genişlikte. */}
      <Bolum baslik="Künye" testId="aksiyon-kunye">
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
          <Alan etiket="İşyeri" deger={aksiyon.workplaceAd} />
          <Alan etiket="Kaynak" deger={aksiyon.kaynakTipiAd} />
          {/* T1 — nitelik kaynaktan türetilir; veremeyen kaynakta alan HİÇ çizilmez (uydurulmaz). */}
          {nitelik && (
            <Alan
              etiket="Nitelik"
              deger={AKSIYON_NITELIK_ETIKETI[nitelik]}
              testId="aksiyon-nitelik"
            />
          )}
          <Alan etiket="Atayan" deger={aksiyon.atayanEtiket} />
          <Alan
            etiket="Atanan"
            deger={
              // A2 — beyan, gerçek bir kullanıcı gibi gösterilmez: bu ada
              // bildirim gitmez, bu kişi kaydı kendi ekranında görmez.
              aksiyon.devirSorumluMetni
                ? `${aksiyon.atananEtiket} (devir beyanı — sistemde hesabı yok)`
                : aksiyon.atananEtiket
            }
            testId="aksiyon-atanan"
          />
          <Alan etiket="Termin" deger={tarihGoster(aksiyon.terminTarihi)} />
          {/* V4 — İLK TERMİN yalnız DEĞİŞMİŞSE gösterilir: değişmemişken
              aynı tarihi iki kez yazmak künyeyi gürültüye boğardı. */}
          {aksiyon.ilkTerminTarihi !== aksiyon.terminTarihi && (
            <Alan
              etiket="İlk termin"
              deger={tarihGoster(aksiyon.ilkTerminTarihi)}
              testId="aksiyon-ilk-termin"
            />
          )}
          <Alan etiket="Açılış" deger={zamanGoster(aksiyon.createdAt)} />
          {/* A4 — DEVREDİLEN KAYIT GEÇMİŞTE KAPANMIŞ OLABİLİR: künye o
              tarihi gösterir. Alan yalnız kapanmış kayıtta çizilir. */}
          {aksiyon.kapanisAt && (
            <Alan
              etiket="Kapanış"
              deger={zamanGoster(aksiyon.kapanisAt)}
              testId="aksiyon-kapanis"
            />
          )}
        </SimpleGrid>
        {/* A4/A5 — DEVİR ŞERHİ. ⚠️ KAPANIŞ MÜŞTERİ BEYANIDIR ve bunu
            kaydın kendi sayfası söyler: sistem onay aramadı, aramadığını
            da saklamıyor. Sorumluluk şerhi Kapsam §22.3/20'de. */}
        {aksiyon.devirKaydi && (
          <Alert color={ANLAM.bilgi} mt="sm" data-testid="aksiyon-devir-serhi">
            Bu kayıt sisteme geçmeden önce tutulan bir kayıttan devredildi.
            {aksiyon.durum === 'closed' &&
              ' Kapanışı müşteri beyanıdır — sistemde bir kapanış onayı kaydı yoktur.'}
          </Alert>
        )}
        {aksiyon.iptalGerekcesi && (
          <Alert color={ANLAM.engel} mt="sm" data-testid="aksiyon-iptal-gerekcesi">
            İptal edildi: {aksiyon.iptalGerekcesi}
          </Alert>
        )}
        {aksiyon.aciklama && (
          <Stack gap={2} mt="sm">
            <Text size="xs" c="dimmed">
              Açıklama
            </Text>
            <Text size="sm" data-testid="aksiyon-aciklama-metni">
              {aksiyon.aciklama}
            </Text>
          </Stack>
        )}
      </Bolum>

      <KaynakBolumu aksiyon={aksiyon} />

      {aksiyon.tamamlanmaAciklamasi && (
        <Bolum baslik="Tamamlanma bildirimi" testId="aksiyon-tamamlanma">
          <Text size="sm">{aksiyon.tamamlanmaAciklamasi}</Text>
        </Bolum>
      )}

      <Bolum baslik="Kanıt ekleri" testId="aksiyon-ekler">
        <AksiyonEkBolumu aksiyon={aksiyon} />
      </Bolum>

      <IzBolumu aksiyonId={aksiyon.id} />

      {acikForm && (
        <DuzeltmeFormu aksiyon={aksiyon} islem={acikForm} onKapat={() => setAcikForm(null)} />
      )}
    </KayitDetayi>
  );
}

function Alan({
  etiket,
  deger,
  testId,
}: {
  etiket: string;
  deger: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">
        {etiket}
      </Text>
      <Text size="sm" data-testid={testId}>
        {deger}
      </Text>
    </Stack>
  );
}

type DuzeltmeIslemi = 'reassign' | 'cancel' | 'change-deadline' | 'edit';

const DUZELTME_BASLIGI: Record<DuzeltmeIslemi, string> = {
  reassign: 'Sorumluyu değiştir',
  cancel: 'Aksiyonu iptal et',
  'change-deadline': 'Termini değiştir',
  edit: 'Başlığı düzelt',
};

/**
 * V6 — DÜZELTME FORMU. Dört işlem tek bileşende çünkü **kuralları aynı**
 * (atayan veya üst merci; iz kaydı; gerekçe alanı) — dört ayrı modal, aynı
 * cümlenin dört kopyası olurdu.
 * ⚠️ **İPTAL ve TERMİNDE GEREKÇE ZORUNLUDUR** (§22.3/12): ikisi de bir
 * KARARDIR. Yeniden atama ve başlık düzeltmede isteğe bağlıdır.
 * ⚠️ **HATA GÖRÜNÜR** (§2.40): sunucunun reddi yutulmaz, form kapanmaz.
 */
function DuzeltmeFormu({
  aksiyon,
  islem,
  onKapat,
}: {
  aksiyon: ActionDetail;
  islem: DuzeltmeIslemi;
  onKapat: () => void;
}): React.JSX.Element {
  const duzelt = useAksiyonDuzeltme();
  const atanabilirler = useAtanabilirler({ enabled: islem === 'reassign' });
  const [gerekce, setGerekce] = useState('');
  const [atanan, setAtanan] = useState<string | null>(null);
  const [termin, setTermin] = useState(aksiyon.terminTarihi);
  const [baslik, setBaslik] = useState(aksiyon.baslik);

  const gerekceZorunlu = islem === 'cancel' || islem === 'change-deadline';
  const hazir =
    (!gerekceZorunlu || gerekce.trim().length > 0) &&
    (islem !== 'reassign' || Boolean(atanan)) &&
    (islem !== 'change-deadline' || gunGirdisiIso(termin) !== null) &&
    (islem !== 'edit' || baslik.trim().length > 0);

  return (
    <Modal opened onClose={onKapat} title={DUZELTME_BASLIGI[islem]} size="md">
      <Stack>
        {islem === 'cancel' && (
          <Alert color={ANLAM.dikkat}>
            İptal geri alınamaz ve bir KAPANIŞ DEĞİLDİR: bu aksiyon raporda ayrı sayılır, bağlı
            olduğu kaydın DÖF sayacından düşer. Gerekirse yerine yeni bir aksiyon açın.
          </Alert>
        )}
        {islem === 'reassign' && (
          <Select
            label="Yeni sorumlu"
            data-testid="duzeltme-atanan"
            value={atanan}
            onChange={setAtanan}
            searchable
            data={(atanabilirler.data ?? []).map((k) => ({
              value: k.appUserId,
              label: k.etiket,
            }))}
          />
        )}
        {islem === 'change-deadline' && (
          <TextInput
            type="date"
            label="Yeni termin"
            data-testid="duzeltme-termin"
            value={termin}
            onChange={(e) => setTermin(e.currentTarget.value)}
          />
        )}
        {islem === 'edit' && (
          <TextInput
            label="Başlık"
            data-testid="duzeltme-baslik"
            value={baslik}
            onChange={(e) => setBaslik(e.currentTarget.value)}
          />
        )}
        <Textarea
          label="Gerekçe"
          description={gerekceZorunlu ? 'Zorunlu' : 'İsteğe bağlı'}
          data-testid="duzeltme-gerekce"
          minRows={2}
          value={gerekce}
          onChange={(e) => setGerekce(e.currentTarget.value)}
        />
        {duzelt.isError && (
          <Alert color={ANLAM.engel} role="alert" data-testid="duzeltme-hata">
            {duzelt.error.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onKapat}>
            Vazgeç
          </Button>
          <Button
            data-testid="duzeltme-kaydet"
            disabled={!hazir || duzelt.isPending}
            onClick={() =>
              duzelt.mutate(
                {
                  id: aksiyon.id,
                  islem,
                  govde: duzeltmeGovdesi(islem, { gerekce, atanan, termin, baslik }),
                },
                { onSuccess: onKapat },
              )
            }
          >
            Kaydet
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function duzeltmeGovdesi(
  islem: DuzeltmeIslemi,
  alanlar: { gerekce: string; atanan: string | null; termin: string; baslik: string },
): unknown {
  const gerekce = alanlar.gerekce.trim() ? alanlar.gerekce.trim() : null;
  if (islem === 'reassign') return { atananAppUserId: alanlar.atanan, gerekce };
  if (islem === 'cancel') return { gerekce: alanlar.gerekce.trim() };
  if (islem === 'change-deadline') {
    return { terminTarihi: alanlar.termin, gerekce: alanlar.gerekce.trim() };
  }
  return { baslik: alanlar.baslik.trim() };
}

/**
 * V6 — GEÇİŞ GEÇMİŞİ: durum geçişleri VE düzeltmeler AYNI listede,
 * kronolojik (tek log — ERD §6). ⚠️ **BOŞ GEÇMİŞ SESSİZ KALMAZ:** iz kaydı
 * Tur 2½b'de doğdu ve öncesindeki geçişler için satır UYDURULMADI; ekran
 * bunu adıyla söyler (uydurma satır denetim izini yanıltırdı).
 */
function IzBolumu({ aksiyonId }: { aksiyonId: string }): React.JSX.Element {
  const iz = useAksiyonIzi(aksiyonId);
  const satirlar = iz.data ?? [];
  return (
    <CokenBolum
      baslik="Geçiş geçmişi"
      sayi={satirlar.length}
      ekleyebilir={false}
      testId="aksiyon-iz"
    >
      {/* ⚠️ Açık hâlde de `aksiyon-iz` testId'si taşınmalı: `CokenBolum`
          çökük kabı `-cokuk` ekiyle işaretler, açık hâlde children'ı OLDUĞU
          GİBİ döner — sarmalamazsak bölümün açık hâli adsız kalır. */}
      <Bolum baslik="Geçiş geçmişi" testId="aksiyon-iz">
        <Stack gap="xs">
          {satirlar.length === 0 && (
            <Text size="sm" c="dimmed">
              Bu aksiyon için iz kaydı yok — iz tutma bu sürümle başladı, daha eski geçişler kayıtlı
              değil.
            </Text>
          )}
          {satirlar.map((satir) => (
            <Stack key={satir.id} gap={0} data-testid="aksiyon-iz-satiri">
              <Text size="sm">
                {AKSIYON_IZ_ETIKETI[satir.islem as keyof typeof AKSIYON_IZ_ETIKETI] ?? satir.islem}
                {satir.eskiDeger && satir.yeniDeger
                  ? `: ${satir.eskiDeger} → ${satir.yeniDeger}`
                  : ''}
              </Text>
              <Text size="xs" c="dimmed">
                {zamanGoster(satir.createdAt)} · {satir.yapanEtiket}
                {satir.gerekce ? ` · ${satir.gerekce}` : ''}
              </Text>
            </Stack>
          ))}
        </Stack>
      </Bolum>
    </CokenBolum>
  );
}

/**
 * T3 — KAYNAK BAĞI BÖLÜMÜ. *"Kayıtlar arası bağ"* bu ürünün farklılaşma
 * tezidir; bağı kurup kullanıcıya göstermemek tezi yarım bırakır.
 *
 * ⚠️ Bağsız (DOĞRUDAN) aksiyonda bölüm ÇİZİLMEZ — boş bir "kaynak kaydı yok"
 * kartı, doğrudan aksiyonu eksikmiş gibi gösterirdi (doğrudan aksiyon
 * meşrudur: kurul kararı · ölçüm · ISO denetimi).
 */
function KaynakBolumu({ aksiyon }: { aksiyon: ActionDetail }): React.JSX.Element | null {
  const baglar: { etiket: string; metin: string; yol?: string; testId: string }[] = [];
  if (aksiyon.incidentId) {
    baglar.push({
      etiket: 'Olay kaydı',
      metin: aksiyon.incidentTakipNo ?? aksiyon.incidentId,
      yol: `/olaylar/${aksiyon.incidentId}`,
      testId: 'kaynak-olay',
    });
  }
  if (aksiyon.incidentCauseId && aksiyon.incidentCauseEtiket) {
    // Sebep düğümünün KENDİ sayfası yoktur (alt kalemdir — P1); bağlantı
    // olayın sebep ağacına gider, metin hangi düğüm olduğunu söyler.
    baglar.push({
      etiket: 'Sebep',
      metin: aksiyon.incidentCauseEtiket,
      testId: 'kaynak-sebep',
    });
  }
  if (aksiyon.riskItemId && aksiyon.riskItemFaaliyet) {
    baglar.push({
      etiket: 'Risk maddesi',
      metin: aksiyon.riskItemFaaliyet,
      testId: 'kaynak-risk',
    });
  }
  if (aksiyon.trainingSessionId && aksiyon.trainingEtiket) {
    baglar.push({
      etiket: 'Eğitim kaydı',
      metin: aksiyon.trainingEtiket,
      yol: `/egitimler/${aksiyon.trainingSessionId}`,
      testId: 'kaynak-egitim',
    });
  }
  if (aksiyon.nonconformityId) {
    // ALTINCI kol (Tur 2½a): uygunsuzluğun KENDİ SAYFASI VARDIR — bağlantı
    // ölü değil, gerçek (form kaydının aksine).
    baglar.push({
      etiket: 'Uygunsuzluk',
      metin: aksiyon.nonconformityTakipNo ?? aksiyon.nonconformityId,
      yol: `/uygunsuzluklar/${aksiyon.nonconformityId}`,
      testId: 'kaynak-uygunsuzluk',
    });
  }
  if (aksiyon.formSubmissionId && aksiyon.formSablonAd) {
    baglar.push({
      etiket: 'Form kaydı',
      metin: aksiyon.formSablonAd,
      // ⚠️ BAĞLANTI YOK ve bu bilinçlidir: form KAYDININ kendi sayfası
      // henüz yoktur (kayıtlar Formlar ekranında listelenir). Ölü bir
      // bağlantı, bağlantı olmamasından kötüdür — §2.40'ın "basılınca
      // hiçbir şey olmaz" kolu. Sayfa açıldığında `yol` buraya eklenir.
      testId: 'kaynak-form',
    });
  }
  if (baglar.length === 0 && !aksiyon.kaynakReferansNotu) return null;

  return (
    <Bolum
      baslik="Kaynak kaydı"
      aciklama="Bu aksiyonun hangi kayıttan doğduğu — kayıtlar arası bağ."
      testId="aksiyon-kaynak-bagi"
    >
      <Stack gap="xs">
        {baglar.map((bag) => (
          <Group key={bag.testId} gap="xs">
            <Text size="xs" c="dimmed" w={110}>
              {bag.etiket}
            </Text>
            {bag.yol ? (
              <Anchor component={Link} to={bag.yol} size="sm" data-testid={bag.testId}>
                {bag.metin}
              </Anchor>
            ) : (
              <Text size="sm" data-testid={bag.testId}>
                {bag.metin}
              </Text>
            )}
          </Group>
        ))}
        {aksiyon.kaynakReferansNotu && (
          <Group gap="xs">
            <Text size="xs" c="dimmed" w={110}>
              Referans notu
            </Text>
            <Text size="sm" data-testid="kaynak-referans-notu">
              {aksiyon.kaynakReferansNotu}
            </Text>
          </Group>
        )}
      </Stack>
    </Bolum>
  );
}
